import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  PlayCircleOutlined,
  ReloadOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import useTerms from "../../../hooks/common/useTerms";
import useAggregationRuns from "../../../hooks/academics/useAggregationRuns";

const RUN_STATUSES = ["Pending", "Running", "Completed", "Failed"];

const statusTag = (status) => {
  switch (status) {
    case "Pending":
      return (
        <Tag icon={<ClockCircleOutlined />} color="gold">
          Pending
        </Tag>
      );
    case "Running":
      return (
        <Tag icon={<SyncOutlined spin />} color="blue">
          Running
        </Tag>
      );
    case "Completed":
      return (
        <Tag icon={<CheckCircleOutlined />} color="green">
          Completed
        </Tag>
      );
    case "Failed":
      return (
        <Tag icon={<CloseCircleOutlined />} color="red">
          Failed
        </Tag>
      );
    default:
      return <Tag>{status}</Tag>;
  }
};

const formatDate = (value) =>
  value ? dayjs(value).format("DD MMM YYYY HH:mm:ss") : "-";

const AggregationRunsManagement = () => {
  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();
  const { runs, loading, fetchRuns, triggerRun, pollRun } =
    useAggregationRuns();

  const [filters, setFilters] = useState({});
  const [triggerModalOpen, setTriggerModalOpen] = useState(false);
  const [triggerAcadyrID, setTriggerAcadyrID] = useState(null);
  const [triggerTermID, setTriggerTermID] = useState(null);

  useEffect(() => {
    fetchAcademicYears();
    fetchTerms();
  }, [fetchAcademicYears, fetchTerms]);

  const load = useCallback(async () => {
    const list = await fetchRuns({
      status: filters.status,
      termID: filters.termID,
      acadyrID: filters.acadyrID,
    });
    const active = list.find(
      (r) => r.status === "Pending" || r.status === "Running",
    );
    if (active) pollRun(active.id);
  }, [fetchRuns, pollRun, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const activeRun = runs.find(
    (r) => r.status === "Pending" || r.status === "Running",
  );

  const handleTrigger = async () => {
    const run = await triggerRun(
      { termID: triggerTermID, acadyrID: triggerAcadyrID },
      () => load(),
    );
    if (run) {
      setTriggerModalOpen(false);
      setTriggerAcadyrID(null);
      setTriggerTermID(null);
    }
  };

  const columns = [
    { title: "ID", dataIndex: "id", width: 70 },
    { title: "Academic Year", dataIndex: "academicYear", width: 130 },
    { title: "Term", dataIndex: "termName", width: 110 },
    { title: "Status", dataIndex: "status", width: 130, render: statusTag },
    {
      title: "Triggered By",
      dataIndex: "triggeredByName",
      width: 160,
      render: (v) => v || "-",
    },
    {
      title: "Started",
      dataIndex: "startedAt",
      width: 180,
      render: formatDate,
    },
    {
      title: "Completed",
      dataIndex: "completedAt",
      width: 180,
      render: formatDate,
    },
    {
      title: "Processed",
      dataIndex: "recordsProcessed",
      width: 100,
      render: (v, record) =>
        record.status === "Completed" || record.status === "Failed" ? v : "-",
    },
    {
      title: "Skipped",
      dataIndex: "recordsSkipped",
      width: 90,
      render: (v, record) => {
        if (record.status !== "Completed" && record.status !== "Failed")
          return "-";
        return v > 0 ? (
          <Tooltip title="Incomplete or draft scoresheets excluded from aggregation">
            <Tag color="orange">{v}</Tag>
          </Tooltip>
        ) : (
          v
        );
      },
    },
  ];

  return (
    <Card
      title="Academic Aggregation Runs"
      extra={
        <Space>
          <Select
            placeholder="Academic Year"
            allowClear
            style={{ width: 160 }}
            value={filters.acadyrID}
            onChange={(v) => setFilters((prev) => ({ ...prev, acadyrID: v }))}
            options={academicYears.map((y) => ({
              value: y.id,
              label: y.acadyr,
            }))}
          />
          <Select
            placeholder="Term"
            allowClear
            style={{ width: 130 }}
            value={filters.termID}
            onChange={(v) => setFilters((prev) => ({ ...prev, termID: v }))}
            options={terms.map((t) => ({ value: t.id, label: t.name }))}
          />
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 130 }}
            value={filters.status}
            onChange={(v) => setFilters((prev) => ({ ...prev, status: v }))}
            options={RUN_STATUSES.map((s) => ({ value: s, label: s }))}
          />
          <Button icon={<ReloadOutlined />} onClick={load}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            disabled={!!activeRun}
            onClick={() => setTriggerModalOpen(true)}
          >
            Run Aggregation
          </Button>
        </Space>
      }
    >
      {activeRun && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          icon={<SyncOutlined spin />}
          message={`Aggregation run #${activeRun.id} is ${activeRun.status.toLowerCase()} for ${activeRun.academicYear} — ${activeRun.termName}`}
          description="The status updates automatically. New runs are blocked until this one finishes."
        />
      )}

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={runs}
        pagination={{ pageSize: 15, showSizeChanger: false }}
        expandable={{
          rowExpandable: (record) =>
            record.status === "Failed" && !!record.errorLog,
          expandedRowRender: (record) => (
            <Alert
              type="error"
              message="Error Log"
              description={
                <Typography.Paragraph
                  style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}
                >
                  {record.errorLog}
                </Typography.Paragraph>
              }
            />
          ),
        }}
      />

      <Modal
        title="Trigger Aggregation Run"
        open={triggerModalOpen}
        onCancel={() => setTriggerModalOpen(false)}
        onOk={handleTrigger}
        okText="Trigger Run"
        okButtonProps={{
          disabled: !triggerAcadyrID || !triggerTermID,
          loading,
        }}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Alert
            type="warning"
            showIcon
            message="This aggregates all submitted scoresheets for the selected term and regenerates analysis reports."
          />
          <Select
            placeholder="Academic Year"
            style={{ width: "100%" }}
            value={triggerAcadyrID}
            onChange={setTriggerAcadyrID}
            options={academicYears.map((y) => ({
              value: y.id,
              label: y.acadyr,
            }))}
          />
          <Select
            placeholder="Term"
            style={{ width: "100%" }}
            value={triggerTermID}
            onChange={setTriggerTermID}
            options={terms.map((t) => ({ value: t.id, label: t.name }))}
          />
        </Space>
      </Modal>
    </Card>
  );
};

export default AggregationRunsManagement;
